import { ActionTree } from 'vuex';

// Shared types
import { RequestSpace, OfferRequestAdditionalProduct } from '@coldtec/cos-types';

// Store types
import {
    RootState,
    RequestAction,
    RequestMutation,
    ToolSpace,
    ProductsAction,
    SystemsAction,
    SpacesAction,
} from '@/store/store.types';

// Api
import Api from '@/api';

/**
 *  Holding the actions of the request.
 */
export const Actions: ActionTree<RootState, RootState> = {

    /**
     *  Used to fetch and (re)store the request of the forwarded guid.
     */
    async [RequestAction.FETCH]({ commit, dispatch }, requestGuid: string): Promise<void> {

        /**
         *  Fetch and (re)store the data of the request.
         */
        const { data } = await Api.client.get(`/requests/${ requestGuid }`);

        commit(RequestMutation.REQUEST, data);

        /**
         *  A returned request is valid by default.
         */
        commit(RequestMutation.VALID, true);

        /**
         *  Dispatch the action to sync the tool spaces.
         */
        dispatch(SpacesAction.SYNC_FROM_REQUEST);

        /**
         *  Dispatch the action to sync the tool products.
         */
        dispatch(ProductsAction.SYNC_FROM_REQUEST);

        /**
         *
         *  Fetch the suggestions that were made for the spaces again.
         *
         *  (!) Must happen after the tool spaces have been synced,
         *  because it uses the tool spaces to fetch suggestions
         *
         */
        await dispatch(SystemsAction.FETCH_SUGGESTIONS);

        return Promise.resolve();

    },

    /**
     *  Used to sync the spaces added spaces (refs).
     */
    [RequestAction.SYNC_SPACES]({ state, commit }): void {

        const spaces: Array<RequestSpace> = [];

        for (const space of Object.values<ToolSpace>(state.spaces.added)) {

            spaces.push(space.data as RequestSpace);

        }

        commit(RequestMutation.SPACES, spaces);

    },

    /**
     *  Used to sync the selected products.
     */
    [RequestAction.SYNC_PRODUCTS]({ state, commit }): void {

        /**
         *
         *  Syncing the selected service products.
         *
         *  (i) Providing an Array of guids of the selected service products
         *
         */
        const serviceProductGuids: Array<string> = [];

        for (const serviceProduct of state.products.service) {

            if (!serviceProduct.selected) {

                continue;

            }

            serviceProductGuids.push(serviceProduct.guid);

        }

        commit(RequestMutation.PRODUCTS_SERVICE, serviceProductGuids);

        /**
         *
         *  Syncing the selected additional products.
         *
         *  (i) Providing an Array of objects that holds the guid
         *  of the selected additional product and the amount.
         *
         */
        const additionalProducts: Array<OfferRequestAdditionalProduct> = [];

        for (const additionalProduct of state.products.additional) {

            if (!additionalProduct.selected) {

                continue;

            }

            /**
             *  Construct the default product.
             */
            const product: OfferRequestAdditionalProduct = {
                guid: additionalProduct.guid,
                amount: additionalProduct.amount,
                price: null,
            };

            /**
             *  Only add the price if there is a price.
             */
            if (additionalProduct.price !== null) {

                product.price = additionalProduct.price;

            }

            additionalProducts.push(product);

        }

        commit(RequestMutation.PRODUCTS_ADDITIONAL, additionalProducts);

    },

};
